import type { AuthoringBlockInstance, BlockPaletteItem } from '@/components/authoring'
import type { ClassifierOutputContract } from '@/hooks/use-classifiers-admin'

export type ClassifierDraftPayload = {
  label?: string
  trigger?: string
  debounce_seconds?: number
  prompts?: {
    system?: string
    followup_guidance?: string
    system_followup?: string
  }
  field_help?: Record<string, string>
  enum_labels?: Record<string, Record<string, string>>
  safety?: { notes?: string; forbidden_patterns?: string[] }
  model_hint?: string | null
  [key: string]: unknown
}

export const CLASSIFIER_BLOCK = {
  contractOverview: 'contract_overview',
  systemGuidance: 'system_guidance',
  followupGuidance: 'followup_guidance',
  safetyNotes: 'safety_notes',
  fieldHelp: 'field_help',
  enumLabels: 'enum_labels',
} as const

function newId(prefix: string) {
  return `${prefix}-${crypto.randomUUID().slice(0, 8)}`
}

/** Keep role/mission; strip legacy schema blobs after "Reply with JSON". */
export function extractRoleProse(system: string): string {
  const text = (system || '').trim()
  if (!text) return ''
  const match = /reply\s+with\s+json/i.exec(text)
  if (match && match.index != null && match.index > 0) {
    return text.slice(0, match.index).trim()
  }
  return text
}

export function payloadToBlocks(
  payload: ClassifierDraftPayload,
  contract: ClassifierOutputContract | null | undefined,
): AuthoringBlockInstance[] {
  const blocks: AuthoringBlockInstance[] = [
    {
      id: 'contract-overview',
      type: CLASSIFIER_BLOCK.contractOverview,
      pinned: true,
      params: {},
    },
  ]

  const system = extractRoleProse(String(payload.prompts?.system || ''))
  if (system) {
    blocks.push({
      id: newId('system'),
      type: CLASSIFIER_BLOCK.systemGuidance,
      params: { text: system },
    })
  }

  const followup = String(
    payload.prompts?.followup_guidance || payload.prompts?.system_followup || '',
  )
  if (followup && (contract?.has_followup_guidance ?? true)) {
    blocks.push({
      id: newId('followup'),
      type: CLASSIFIER_BLOCK.followupGuidance,
      params: { text: followup },
    })
  }

  const notes = String(payload.safety?.notes || '')
  if (notes) {
    blocks.push({
      id: newId('safety'),
      type: CLASSIFIER_BLOCK.safetyNotes,
      params: { text: notes },
    })
  }

  const help = payload.field_help || {}
  for (const [key, value] of Object.entries(help)) {
    if (!String(value || '').trim()) continue
    // Skip if identical to contract default — still show when author overrides or seeded
    blocks.push({
      id: newId(`help-${key}`),
      type: CLASSIFIER_BLOCK.fieldHelp,
      params: { field: key, text: String(value) },
    })
  }

  const enums = payload.enum_labels || contract?.enum_labels || {}
  for (const [field, labels] of Object.entries(enums)) {
    if (!labels || typeof labels !== 'object') continue
    blocks.push({
      id: newId(`enum-${field}`),
      type: CLASSIFIER_BLOCK.enumLabels,
      params: { field, labels: { ...labels } },
    })
  }

  // Ensure at least system guidance exists for editing
  if (!blocks.some((b) => b.type === CLASSIFIER_BLOCK.systemGuidance)) {
    blocks.push({
      id: newId('system'),
      type: CLASSIFIER_BLOCK.systemGuidance,
      params: { text: '' },
    })
  }

  return blocks
}

export function blocksToPayload(
  blocks: AuthoringBlockInstance[],
  base: ClassifierDraftPayload,
  meta: { label: string; debounceSeconds: number },
): ClassifierDraftPayload {
  const prompts: ClassifierDraftPayload['prompts'] = {
    ...(base.prompts || {}),
  }
  let system = ''
  let followup = ''
  let safetyNotes = ''
  const field_help: Record<string, string> = {}
  const enum_labels: Record<string, Record<string, string>> = {}

  for (const block of blocks) {
    if (block.type === CLASSIFIER_BLOCK.systemGuidance) {
      system = String(block.params.text ?? '')
    } else if (block.type === CLASSIFIER_BLOCK.followupGuidance) {
      followup = String(block.params.text ?? '')
    } else if (block.type === CLASSIFIER_BLOCK.safetyNotes) {
      safetyNotes = String(block.params.text ?? '')
    } else if (block.type === CLASSIFIER_BLOCK.fieldHelp) {
      const field = String(block.params.field ?? '')
      if (field) field_help[field] = String(block.params.text ?? '')
    } else if (block.type === CLASSIFIER_BLOCK.enumLabels) {
      const field = String(block.params.field ?? '')
      const labels = block.params.labels
      if (field && labels && typeof labels === 'object') {
        enum_labels[field] = { ...(labels as Record<string, string>) }
      }
    }
  }

  prompts.system = system
  if (followup) {
    prompts.followup_guidance = followup
    delete prompts.system_followup
  } else {
    delete prompts.followup_guidance
    delete prompts.system_followup
  }

  return {
    ...base,
    label: meta.label.trim() || base.label,
    debounce_seconds: meta.debounceSeconds,
    trigger: base.trigger || 'debounced',
    prompts,
    field_help,
    enum_labels,
    safety: {
      ...(base.safety || {}),
      notes: safetyNotes,
    },
  }
}

export function classifierPaletteItems(opts: {
  blocks: AuthoringBlockInstance[]
  contract: ClassifierOutputContract | null | undefined
  profileId: string
}): BlockPaletteItem[] {
  const { blocks, contract, profileId } = opts
  const present = new Set(blocks.map((b) => b.type))
  const hasFollowup =
    Boolean(contract?.has_followup_guidance) || profileId === 'vet_call_urgency'

  const fieldKeys = Object.keys(contract?.output_json_schema?.properties || {})
  const helpFieldsPresent = new Set(
    blocks
      .filter((b) => b.type === CLASSIFIER_BLOCK.fieldHelp)
      .map((b) => String(b.params.field || '')),
  )
  const enumFieldsPresent = new Set(
    blocks
      .filter((b) => b.type === CLASSIFIER_BLOCK.enumLabels)
      .map((b) => String(b.params.field || '')),
  )
  const enumFields = Object.keys(contract?.enum_labels || {})

  const items: BlockPaletteItem[] = [
    {
      type: CLASSIFIER_BLOCK.systemGuidance,
      label: 'Role / mission',
      description: 'Short role prose — schema is composed from the contract',
      singleton: true,
      disabled: present.has(CLASSIFIER_BLOCK.systemGuidance),
      disabledReason: present.has(CLASSIFIER_BLOCK.systemGuidance)
        ? 'Already in document'
        : undefined,
    },
  ]

  if (hasFollowup) {
    items.push({
      type: CLASSIFIER_BLOCK.followupGuidance,
      label: 'Follow-up guidance',
      description: 'Prose for later turns (prior state is injected)',
      singleton: true,
      disabled: present.has(CLASSIFIER_BLOCK.followupGuidance),
      disabledReason: present.has(CLASSIFIER_BLOCK.followupGuidance)
        ? 'Already in document'
        : undefined,
    })
  }

  items.push({
    type: CLASSIFIER_BLOCK.safetyNotes,
    label: 'Safety notes',
    description: 'Author-facing safety prose',
    singleton: true,
    disabled: present.has(CLASSIFIER_BLOCK.safetyNotes),
    disabledReason: present.has(CLASSIFIER_BLOCK.safetyNotes)
      ? 'Already in document'
      : undefined,
  })

  for (const field of fieldKeys) {
    items.push({
      type: `${CLASSIFIER_BLOCK.fieldHelp}:${field}`,
      label: `Field help · ${field}`,
      description: 'Override help text for this output field',
      disabled: helpFieldsPresent.has(field),
      disabledReason: helpFieldsPresent.has(field) ? 'Already in document' : undefined,
    })
  }

  for (const field of enumFields) {
    items.push({
      type: `${CLASSIFIER_BLOCK.enumLabels}:${field}`,
      label: `Enum labels · ${field}`,
      description: 'Display labels for enum values',
      disabled: enumFieldsPresent.has(field),
      disabledReason: enumFieldsPresent.has(field) ? 'Already in document' : undefined,
    })
  }

  return items
}

export function createBlockFromPaletteType(
  paletteType: string,
  contract: ClassifierOutputContract | null | undefined,
): AuthoringBlockInstance | null {
  if (paletteType === CLASSIFIER_BLOCK.systemGuidance) {
    return { id: newId('system'), type: CLASSIFIER_BLOCK.systemGuidance, params: { text: '' } }
  }
  if (paletteType === CLASSIFIER_BLOCK.followupGuidance) {
    return { id: newId('followup'), type: CLASSIFIER_BLOCK.followupGuidance, params: { text: '' } }
  }
  if (paletteType === CLASSIFIER_BLOCK.safetyNotes) {
    return { id: newId('safety'), type: CLASSIFIER_BLOCK.safetyNotes, params: { text: '' } }
  }
  if (paletteType.startsWith(`${CLASSIFIER_BLOCK.fieldHelp}:`)) {
    const field = paletteType.slice(CLASSIFIER_BLOCK.fieldHelp.length + 1)
    const defaultHelp = contract?.field_help?.[field] ?? ''
    return {
      id: newId(`help-${field}`),
      type: CLASSIFIER_BLOCK.fieldHelp,
      params: { field, text: defaultHelp },
    }
  }
  if (paletteType.startsWith(`${CLASSIFIER_BLOCK.enumLabels}:`)) {
    const field = paletteType.slice(CLASSIFIER_BLOCK.enumLabels.length + 1)
    const labels = { ...(contract?.enum_labels?.[field] || {}) }
    return {
      id: newId(`enum-${field}`),
      type: CLASSIFIER_BLOCK.enumLabels,
      params: { field, labels },
    }
  }
  return null
}
